"use client";
import { useSearchParams } from "next/navigation";
import { FetchSingleEvent } from "../../components/FetchSingleEvent";
import Button from "../../components/Button";
import { useEffect, useState } from "react";
import { Pinyon_Script, ZCOOL_KuaiLe } from "next/font/google";
const pinyonScript = Pinyon_Script({ subsets: ["latin"], weight: "400" });
import { Josefin_Sans } from "next/font/google";
const josefinSans = Josefin_Sans({ subsets: ["latin"], weight: "400" });
import Image from "next/image";
import classesImg from "../../img/classes.webp";
import eventsImg from "../../img/events.webp";
import { parseCookies } from "nookies";
import Linkify from "react-linkify";

function formatDateAmerican(dateString) {
  if (!dateString) return "";
  const parts = dateString.split("-");
  if (parts.length !== 3) return dateString;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  return `${month}/${day}/${year}`;
}

export function formatTimeToAmPm(timeString) {
  if (!timeString) return "";
  const [hour, minute] = timeString.split(":").map(Number);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minute.toString().padStart(2, "0")} ${ampm}`;
}

export default function ListingContent() {
  const searchParams = useSearchParams();
  const eventId = searchParams.get("eventId");
  const eventType = searchParams.get("eventType");

  const [event, setEvent] = useState(null);
  const [comments, setComments] = useState([]);
  const [imgSrc, setImgSrc] = useState("");
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentContent, setEditingCommentContent] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});
  const [eventOwnerId, setEventOwnerId] = useState(null);
  const [guests, setGuests] = useState(1);
  const [rsvpCount, setRsvpCount] = useState(0);
  const [rsvpMessage, setRsvpMessage] = useState("");
  const [hasRSVPd, setHasRSVPd] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  let pageHref = "/classes";
  if (event?.type?.toLowerCase() === "event") {
    pageHref = "/events";
  }

  // posting state + errors
  const [isPosting, setIsPosting] = useState(false);
  const [commentError, setCommentError] = useState(null);

  const cookies = parseCookies();
  const token = cookies.token;
  const url = process.env.NEXT_PUBLIC_REACT_APP_SERVER;

  // fallbackSrc must be accessible in JSX (don't build it inside loadEvent only)
  const fallbackSrc =
    eventType?.toLowerCase() === "class" ? classesImg : eventsImg;

  // get current user UID (if logged in)
  useEffect(() => {
    async function fetchCurrentUser() {
      if (!token) return;
      try {
        const res = await fetch(`${url}/users/self`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setCurrentUserId(data.uid);
          const isAdmin = data?.permissions?.includes("Admin");
          setIsAdmin(isAdmin);
        }
      } catch (err) {
        console.error("Error fetching current user:", err);
      }
    }
    fetchCurrentUser();
  }, [token, url]);

  // fetch event and then comments
  useEffect(() => {
    async function loadEvent() {
      if (!eventId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await FetchSingleEvent(eventId);
        setEvent(data);
        setEventOwnerId(data.createdBy);
        setImgSrc(data.imgUrl || fallbackSrc);
        // load comments from dedicated comments endpoint for canonical ordering
        await fetchComments();
      } catch (err) {
        console.error("Error loading event:", err);
      } finally {
        setLoading(false);
      }
    }
    loadEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    async function fetchRSVP() {
      try {
        const res = await fetch(`${url}/events/${eventId}`);
        if (res.ok) {
          const data = await res.json();
          const rsvpObj = data.RSVP || {};

          // Total RSVP count
          const count = Object.values(rsvpObj).reduce(
            (sum, entry) => sum + (entry.guests || 0),
            0,
          );
          setRsvpCount(count);

          // Check if current user has RSVP'd
          if (currentUserId) {
            setHasRSVPd(!!rsvpObj[currentUserId]);
            if (rsvpObj[currentUserId]) {
              setGuests(rsvpObj[currentUserId].guests); // populate guests input
            }
          }
        }
      } catch (err) {
        console.error("Error fetching RSVP:", err);
      }
    }

    if (eventId) fetchRSVP();
  }, [eventId, url, currentUserId]);

  async function handleRSVP() {
    if (!token) {
      setRsvpMessage("You must be signed in to RSVP.");
      return;
    }

    const maxRSVPs = event?.max_rsvps;
    const totalAfterRSVP = rsvpCount + Number(guests);
    const spotsLeft = maxRSVPs ? maxRSVPs - rsvpCount : null;

    if (maxRSVPs !== null && totalAfterRSVP > maxRSVPs) {
      setRsvpMessage(
        spotsLeft > 0
          ? `Only ${spotsLeft} spot${
              spotsLeft > 1 ? "s" : ""
            } left — please reduce your guest count.`
          : "This event is fully booked. No spots left.",
      );
      return;
    }

    try {
      const res = await fetch(`${url}/events/${eventId}/rsvp`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ guests: Number(guests) }),
      });

      const body = await res.json();
      if (res.ok) {
        setRsvpMessage(body.message || "RSVP successful!");
        setRsvpCount(body.RSVPcount);
        setHasRSVPd(true);
      } else {
        setRsvpMessage(body.error || "RSVP failed.");
      }
    } catch (err) {
      console.error("RSVP error:", err);
      setRsvpMessage("Network error while RSVPing.");
    }
  }

  async function handleRemoveRSVP() {
    if (!token) {
      setRsvpMessage("You must be signed in to remove an RSVP.");
      return;
    }

    try {
      const res = await fetch(`${url}/events/${eventId}/rsvp`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ guests: 0 }),
      });

      const body = await res.json();

      if (res.ok) {
        setRsvpMessage(body.message || "RSVP removed.");
        setRsvpCount(body.RSVPcount ?? Math.max(rsvpCount - 1, 0));
        setHasRSVPd(false);
      } else {
        setRsvpMessage(body.error || "Failed to remove RSVP.");
      }
    } catch (err) {
      console.error("Error removing RSVP:", err);
      setRsvpMessage("Network error removing RSVP.");
    }
  }

  // fetch comments helper
  async function fetchComments() {
    try {
      const res = await fetch(`${url}/events/${eventId}/comments`);
      if (!res.ok) {
        // maybe event has no comments or server error
        const err = await res.json().catch(() => ({}));
        console.error("Failed fetching comments:", err);
        setComments([]);
        return;
      }
      const data = await res.json();
      // server returns array sorted by createdAt — use it directly
      setComments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching comments:", err);
      setComments([]);
    }
  }

  // add top-level or reply comment
  async function handleAddComment(parentId = null) {
    setCommentError(null);

    const content = parentId ? replyContent : newComment;
    if (!content || !content.trim()) {
      setCommentError("Please enter a comment.");
      return;
    }
    if (!token) {
      setCommentError("You must be signed in to post a comment.");
      return;
    }

    setIsPosting(true);
    try {
      const res = await fetch(`${url}/events/${eventId}/comments`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ content, parentId }),
      });

      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error("POST comment failed:", body);
        setCommentError(body.error || "Failed to post comment.");
        setIsPosting(false);
        return;
      }

      // successful post — refresh comments list from server so nesting is correct
      await fetchComments();

      // clear input(s)
      if (parentId) {
        setReplyContent("");
        setReplyingTo(null);
      } else {
        setNewComment("");
      }
    } catch (err) {
      console.error("Error posting comment:", err);
      setCommentError("Network error posting comment.");
    } finally {
      setIsPosting(false);
    }
  }

  async function handleSaveEdit(commentId) {
    if (!editingCommentContent.trim()) return;
    if (!token) {
      setCommentError("You must be signed in to edit comments.");
      return;
    }
    try {
      const res = await fetch(
        `${url}/events/${eventId}/comments/${commentId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ content: editingCommentContent }),
        },
      );
      if (res.ok) {
        await fetchComments();
        setEditingCommentId(null);
      } else {
        const err = await res.json().catch(() => ({}));
        setCommentError(err.error || "Failed to update comment.");
      }
    } catch (err) {
      console.error("Error updating comment:", err);
      setCommentError("Network error updating comment.");
    }
  }

  async function handleDeleteComment(commentId) {
    if (!token) {
      setCommentError("You must be signed in to delete comments.");
      return;
    }
    if (!confirm("Are you sure you want to delete this comment?")) return;

    try {
      const res = await fetch(
        `${url}/events/${eventId}/comments/${commentId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (res.ok) {
        await fetchComments();
      } else {
        const err = await res.json().catch(() => ({}));
        setCommentError(err.error || "Failed to delete comment.");
      }
    } catch (err) {
      console.error("Error deleting comment:", err);
      setCommentError("Network error deleting comment.");
    }
  }

  // recursive renderer for nested comments
  function renderComments(parentId = null, level = 0) {
    return comments
      .filter((c) => (c.parentId ?? null) === parentId)
      .map((c) => {
        const isExpanded = expandedComments[c.commentId] || false;
        const showReplies = expandedComments[`thread-${c.commentId}`] ?? true; // thread toggle
        const childReplies = comments.filter(
          (r) => (r.parentId ?? null) === c.commentId,
        );

        const maxLength = 200;
        const isLong = c.content && c.content.length > maxLength;
        const displayedContent = isExpanded
          ? c.content
          : c.content && c.content.slice(0, maxLength) + (isLong ? "..." : "");

        return (
          <li
            key={c.commentId}
            style={{ marginLeft: level * 16 }}
            className={`p-4 rounded-lg shadow mb-2 ${
              level === 0 ? "bg-white" : "bg-gray-50 border-l-2 border-gray-200"
            }`}
          >
            <div className="flex-1">
              {editingCommentId === c.commentId ? (
                <div className="flex flex-col gap-2">
                  <textarea
                    className="w-full p-2 border rounded"
                    value={editingCommentContent}
                    onChange={(e) => setEditingCommentContent(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button
                      className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                      onClick={() => handleSaveEdit(c.commentId)}
                    >
                      Save
                    </button>
                    <button
                      className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                      onClick={() => setEditingCommentId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-gray-800">
                    {displayedContent}{" "}
                    {isLong && (
                      <button
                        onClick={() =>
                          setExpandedComments((prev) => ({
                            ...prev,
                            [c.commentId]: !prev[c.commentId],
                          }))
                        }
                        className="text-blue-500 hover:underline text-sm"
                      >
                        {isExpanded ? "See less" : "See more"}
                      </button>
                    )}
                  </p>
                  <span className="text-sm text-gray-500">
                    {c.userName || "Anonymous"} •{" "}
                    {c.createdAt ? new Date(c.createdAt).toLocaleString() : ""}
                  </span>
                </>
              )}
            </div>

            {editingCommentId !== c.commentId && (
              <div className="flex gap-2 mt-2">
                <button
                  className="text-gray-600 hover:text-blue-500 text-sm"
                  onClick={() =>
                    setReplyingTo((prev) =>
                      prev === c.commentId ? null : c.commentId,
                    )
                  }
                >
                  Reply
                </button>

                {c.userId === currentUserId && (
                  <button
                    className="text-gray-600 hover:text-blue-500"
                    onClick={() => {
                      setEditingCommentId(c.commentId);
                      setEditingCommentContent(c.content);
                    }}
                  >
                    <span className="material-icons">edit</span>
                  </button>
                )}

                {(c.userId === currentUserId ||
                  eventOwnerId === currentUserId ||
                  isAdmin) && (
                  <button
                    onClick={() => handleDeleteComment(c.commentId)}
                    className="text-gray-600 hover:text-red-500"
                    title="Delete"
                  >
                    <span className="material-icons">delete</span>
                  </button>
                )}
              </div>
            )}

            {/* Reply box */}
            {replyingTo === c.commentId && (
              <div className="mt-2 flex flex-col gap-2">
                <textarea
                  className="w-full p-2 border rounded"
                  placeholder="Write a reply..."
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAddComment(c.commentId)}
                    disabled={isPosting}
                    className="px-4 py-2 bg-accent text-white rounded disabled:opacity-60"
                  >
                    {isPosting ? "Posting..." : "Submit Reply"}
                  </button>
                  <button
                    onClick={() => {
                      setReplyingTo(null);
                      setReplyContent("");
                    }}
                    className="px-4 py-2 bg-gray-300 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Children (replies) */}
            {childReplies.length > 0 && (
              <div className="mt-3">
                <button
                  onClick={() =>
                    setExpandedComments((prev) => ({
                      ...prev,
                      [`thread-${c.commentId}`]: !showReplies,
                    }))
                  }
                  className="text-sm text-blue-500 hover:underline"
                >
                  {showReplies
                    ? `Hide Replies (${childReplies.length})`
                    : `Show Replies (${childReplies.length})`}
                </button>

                {showReplies && (
                  <ul className="mt-2">
                    {renderComments(c.commentId, level + 1)}
                  </ul>
                )}
              </div>
            )}
          </li>
        );
      });
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-2xl animate-pulse text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-2xl text-red-600">Event not found.</div>
      </div>
    );
  }

  const formattedStart = formatDateAmerican(event.start_date);
  const formattedEnd = formatDateAmerican(event.end_date);
  const dateDisplay = formattedEnd
    ? `${formattedStart} - ${formattedEnd}`
    : formattedStart;
  const formattedStartTime = formatTimeToAmPm(event.start_time);
  const formattedEndTime = formatTimeToAmPm(event.end_time);
  const timeDisplay = formattedEndTime
    ? `${formattedStartTime} - ${formattedEndTime}`
    : formattedStartTime;

  return (
    <div className="py-8">
      <div>
        <div className="text-left mx-auto w-[90%] flex items-center">
          <Button
            href={pageHref}
            className="ml-0 px-8 py-4 flex items-center gap-2 text-base md:text-lg underline"
          >
            <svg
              className="w-4 h-4 transform rotate-90"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
            Back
          </Button>
        </div>

        <div className="flex flex-wrap items-center justify-between w-[90%] mx-auto my-12">
          <div
            onClick={() => setIsOpen(true)}
            className="w-[100%] md:w-[45%] mx-auto relative aspect-[6/5] md:h-[500px] rounded-xl overflow-hidden cursor-pointer"
          >
            <Image
              src={imgSrc}
              onError={() => setImgSrc(fallbackSrc)}
              alt={"Info image"}
              fill
              style={{ objectFit: "contain", borderRadius: "inherit" }}
            />
          </div>

          {isOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
              onClick={() => setIsOpen(false)}
            >
              <div className="w-[95%] md:w-[45%] mx-auto relative aspect-[6/5] md:h-[500px] rounded-xl overflow-hidden cursor-pointer">
                <Image
                  src={imgSrc}
                  onError={() => setImgSrc(fallbackSrc)}
                  alt={"Info image"}
                  fill
                  style={{ objectFit: "contain", borderRadius: "inherit" }}
                />
              </div>
            </div>
          )}

          <div
            className={`${josefinSans.className} w-[95%] md:w-[50%] px-8 rounded-xl mx-auto my-12 py-8 bg-cardYellow`}
          >
            <div className="md:w-[80%] w-[90%] mx-auto break-words box-border">
              <h3 className="text-2xl md:text-4xl py-4">{event.title}</h3>
              {event.ongoing && (
                <p className="text-sm font-semibold uppercase tracking-wide text-green-800 bg-green-100 inline-block px-3 py-1 rounded-full mb-2">
                  Ongoing
                </p>
              )}
              <h3 className="md:text-lg">
                <b>Date: </b> {dateDisplay}
              </h3>
              <h3 className="md:text-lg">
                <b>Time: </b> {timeDisplay}
              </h3>
              <h4 className="md:text-lg">
                <b>Cost: </b>${event.cost}
              </h4>
              <h5 className="md:text-lg">
                <b>Location: </b>{" "}
                {event.location || "Create a free account to see the location"}
              </h5>
              <p className="md:text-lg py-2">
                <b>About: </b>
                <Linkify
                  componentDecorator={(decoratedHref, decoratedText, key) => (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={decoratedHref}
                      key={key}
                      className="text-blue-600 underline"
                    >
                      {decoratedText}
                    </a>
                  )}
                >
                  {event.description}
                </Linkify>
              </p>
              <p className="md:text-lg">
                <b>Contact: </b> {event.contact}
              </p>
              <div className="mx-auto my-8">
                <h3 className="text-2xl mb-4">RSVP</h3>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-4">
                    <input
                      type="number"
                      min="1"
                      value={guests}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setGuests(value);
                        if (event?.max_rsvps) {
                          const spotsLeft = event.max_rsvps - rsvpCount;
                          if (value > spotsLeft) {
                            setRsvpMessage(
                              spotsLeft > 0
                                ? `Only ${spotsLeft} spot${
                                    spotsLeft > 1 ? "s" : ""
                                  } left.`
                                : "This event is fully booked.",
                            );
                          } else {
                            setRsvpMessage("");
                          }
                        }
                      }}
                      className="p-2 border rounded w-24"
                      placeholder="Guests"
                      disabled={hasRSVPd}
                    />

                    <button
                      onClick={hasRSVPd ? handleRemoveRSVP : handleRSVP}
                      disabled={
                        !hasRSVPd &&
                        event?.max_rsvps &&
                        rsvpCount + Number(guests) > event.max_rsvps
                      }
                      className={`px-6 py-2 rounded-lg ${
                        hasRSVPd
                          ? "bg-red-500 hover:bg-red-600 text-white"
                          : !hasRSVPd &&
                              event?.max_rsvps &&
                              rsvpCount >= event.max_rsvps
                            ? "bg-gray-400 text-white cursor-not-allowed"
                            : "bg-accent hover:bg-accentDark text-white"
                      }`}
                    >
                      {hasRSVPd ? "Cancel RSVP" : "RSVP"}
                    </button>
                  </div>
                </div>

                {/* Optional: show a warning if max reached and user hasn't RSVP'd */}
                {!hasRSVPd &&
                  event?.max_rsvps &&
                  rsvpCount >= event.max_rsvps && (
                    <p className="mt-2 text-red-600 text-sm">
                      Maximum RSVPs reached. You cannot RSVP.
                    </p>
                  )}

                {rsvpMessage && (
                  <p className="mt-2 text-sm text-gray-700">{rsvpMessage}</p>
                )}

                <p className="mt-4 text-lg font-medium">
                  Total RSVPs: {rsvpCount}
                </p>
              </div>
              {event.location && (
                <div className="w-full h-[300px] my-4 rounded-xl overflow-hidden shadow-lg">
                  <iframe
                    title="Event Location"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    src={`https://www.google.com/maps?q=${encodeURIComponent(
                      event.location,
                    )}&output=embed`}
                  ></iframe>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="w-[90%] mx-auto my-8 p-6 bg-cardYellow rounded-xl">
          <h3 className="text-2xl mb-4">Comments</h3>

          {commentError && (
            <div className="text-red-600 mb-4">{commentError}</div>
          )}

          {comments.length ? (
            <ul className="space-y-4">{renderComments()}</ul>
          ) : (
            <p className="text-gray-600">
              No comments yet. Be the first to comment!
            </p>
          )}

          {/* Add Comment Input */}
          {!token ? (
            <p className="text-gray-600 mt-4">
              You must be signed in to comment.
            </p>
          ) : (
            <div className="mt-6 flex flex-col md:flex-row gap-2">
              <textarea
                className="flex-1 p-4 rounded-lg border border-gray-300 focus:outline-none focus:ring focus:ring-yellow-300"
                placeholder="Write a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              <button
                onClick={() => handleAddComment(null)}
                disabled={isPosting}
                className="px-6 py-2 bg-accent text-white rounded-lg hover:bg-accentDark disabled:opacity-60 md:self-center"
              >
                {isPosting ? "Posting..." : "Submit"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
